import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  FileCheck2,
  Upload,
  Eye,
  Download,
  ShieldCheck,
  FileText,
  ImageIcon,
  X,
} from "lucide-react";
import type { AxiosResponse } from "axios";
import Button from "../UI/Button";
import { Modal } from "../UI/Modal";
import { SectionCard } from "../UI/SectionCard";
import type { SignedDocument } from "../../types";

/**
 * Signed Documents — permanent, immutable shipment paperwork (e.g. a
 * photographed / scanned paper CMR). Shared between the system side
 * (admin/user) and the driver side; the parent injects the bound upload /
 * download API calls.
 *
 * Flow: pick a file (image or PDF) → PREVIEW (image render / PDF viewer) →
 * explicit CONFIRMATION that the upload is permanent and cannot be edited or
 * deleted → upload. Existing documents are view/download only — there is no
 * delete control anywhere.
 */

interface SignedDocumentsSectionProps {
  documents: SignedDocument[];
  /** Bound upload call (shipmentAPI / driver API). Returns the created docs. */
  uploadFn: (formData: FormData) => Promise<AxiosResponse>;
  /** Bound download/preview call — returns a Blob response. */
  viewFn: (documentId: string, preview: boolean) => Promise<AxiosResponse>;
  /** Called with the freshly-created documents after a successful upload. */
  onUploaded?: (newDocs: SignedDocument[]) => void;
  /** When true, hide the uploader (list stays visible). */
  readonly?: boolean;
}

const isImage = (mime?: string) => !!mime && mime.startsWith("image/");
const isPdf = (mime?: string) => mime === "application/pdf";

const formatFileSize = (bytes: number): string => {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

export const SignedDocumentsSection: React.FC<SignedDocumentsSectionProps> = ({
  documents,
  uploadFn,
  viewFn,
  onUploaded,
  readonly = false,
}) => {
  const { t } = useTranslation("shipments");
  const inputRef = useRef<HTMLInputElement>(null);

  // Staged (pre-confirmation) file + its object-URL preview.
  const [staged, setStaged] = useState<File | null>(null);
  const [stagedUrl, setStagedUrl] = useState<string>("");
  const [uploading, setUploading] = useState(false);

  // Preview of an already-uploaded document.
  const [viewer, setViewer] = useState<{
    url: string;
    mime: string;
    name: string;
  } | null>(null);

  // Revoke object URLs when they change / on unmount.
  useEffect(() => {
    return () => {
      if (stagedUrl) URL.revokeObjectURL(stagedUrl);
    };
  }, [stagedUrl]);
  useEffect(() => {
    return () => {
      if (viewer?.url) URL.revokeObjectURL(viewer.url);
    };
  }, [viewer]);

  const handlePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!isImage(file.type) && !isPdf(file.type)) {
      toast.error(t("signedDocuments.invalidType"));
      return;
    }
    if (stagedUrl) URL.revokeObjectURL(stagedUrl);
    setStaged(file);
    setStagedUrl(URL.createObjectURL(file));
  };

  const cancelStaged = () => {
    if (stagedUrl) URL.revokeObjectURL(stagedUrl);
    setStaged(null);
    setStagedUrl("");
  };

  const confirmUpload = async () => {
    if (!staged) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("signedDocuments", staged);
      const res = await uploadFn(formData);
      const newDocs: SignedDocument[] =
        res?.data?.data?.signedDocuments ?? [];
      toast.success(t("signedDocuments.uploadSuccess"));
      onUploaded?.(newDocs);
      cancelStaged();
    } catch (err) {
      console.error("Signed document upload failed:", err);
      toast.error(t("signedDocuments.uploadFailed"));
    } finally {
      setUploading(false);
    }
  };

  const openDocument = async (doc: SignedDocument) => {
    try {
      const res = await viewFn(doc._id, true);
      const blob = res.data as Blob;
      const url = URL.createObjectURL(blob);
      setViewer({ url, mime: doc.mimeType, name: doc.originalName });
    } catch (err) {
      console.error("Signed document preview failed:", err);
      toast.error(t("signedDocuments.previewFailed"));
    }
  };

  const downloadDocument = async (doc: SignedDocument) => {
    try {
      const res = await viewFn(doc._id, false);
      const blob = res.data as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.originalName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error("Signed document download failed:", err);
      toast.error(t("signedDocuments.downloadFailed"));
    }
  };

  return (
    <SectionCard
      icon={FileCheck2}
      title={t("signedDocuments.title")}
      note={t("signedDocuments.permanentNote")}
    >
      {!readonly && (
        <div className="mb-3">
          <input
            ref={inputRef}
            type="file"
            accept="image/*,application/pdf"
            onChange={handlePick}
            className="hidden"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
          >
            <Upload className="h-3.5 w-3.5 mr-1" />
            {t("signedDocuments.uploadButton")}
          </Button>
        </div>
      )}

      {documents.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          {documents.map((doc) => {
            const Icon = isImage(doc.mimeType) ? ImageIcon : FileText;
            return (
              <div
                key={doc._id}
                className="flex items-center justify-between p-2 bg-gray-50 rounded-md border border-gray-200 gap-3"
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Icon className="h-4 w-4 text-gray-500 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[13px] font-medium text-gray-900 truncate">
                      {doc.originalName}
                    </p>
                    <p className="text-[11px] text-gray-500">
                      {formatFileSize(doc.size)}
                      {doc.uploadedVia
                        ? ` • ${t(`signedDocuments.via.${doc.uploadedVia}`)}`
                        : ""}
                      {doc.uploadedByName ? ` • ${doc.uploadedByName}` : ""}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => openDocument(doc)}
                    className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                    title={t("signedDocuments.view")}
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => downloadDocument(doc)}
                    className="p-1 text-gray-500 hover:text-green-600 transition-colors"
                    title={t("signedDocuments.download")}
                  >
                    <Download className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-5 text-gray-500">
          <FileCheck2 className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          <p className="text-[12px]">{t("signedDocuments.noDocs")}</p>
        </div>
      )}

      {/* Staged preview + permanence confirmation */}
      {staged && (
        <Modal
          isOpen={!!staged}
          onClose={uploading ? () => {} : cancelStaged}
          title={t("signedDocuments.confirmTitle")}
          size="lg"
        >
          <div className="p-4 sm:p-6 overflow-y-auto flex-1 space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
              {isImage(staged.type) ? (
                <img
                  src={stagedUrl}
                  alt={staged.name}
                  className="max-h-[55vh] w-full object-contain bg-[#F5F4F0]"
                />
              ) : (
                <iframe
                  title={staged.name}
                  src={stagedUrl}
                  className="w-full h-[55vh] bg-[#F5F4F0]"
                />
              )}
            </div>
            <div className="text-[13px] text-gray-700">
              <span className="font-medium">{staged.name}</span>{" "}
              <span className="text-gray-400">
                ({formatFileSize(staged.size)})
              </span>
            </div>
            <div className="flex items-start gap-2 rounded-lg bg-[#FAEEDA] border border-[#FAC775] text-[#633806] px-4 py-2.5 text-[13px]">
              <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />
              <div>{t("signedDocuments.permanentWarning")}</div>
            </div>
          </div>
          <div className="px-4 py-3 sm:px-6 border-t border-[rgba(11,15,14,0.08)] bg-white flex justify-end gap-2 shrink-0">
            <Button
              type="button"
              variant="ghost"
              onClick={cancelStaged}
              disabled={uploading}
            >
              <X className="h-4 w-4 mr-1" />
              {t("signedDocuments.cancel")}
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={confirmUpload}
              disabled={uploading}
            >
              <ShieldCheck className="h-4 w-4 mr-1" />
              {uploading
                ? t("signedDocuments.uploading")
                : t("signedDocuments.confirmUpload")}
            </Button>
          </div>
        </Modal>
      )}

      {/* Viewer for an already-uploaded document */}
      {viewer && (
        <Modal
          isOpen={!!viewer}
          onClose={() => setViewer(null)}
          title={viewer.name}
          size="xl"
        >
          <div className="p-2 sm:p-4 overflow-y-auto flex-1">
            {isImage(viewer.mime) ? (
              <img
                src={viewer.url}
                alt={viewer.name}
                className="max-h-[75vh] w-full object-contain bg-[#F5F4F0]"
              />
            ) : (
              <iframe
                title={viewer.name}
                src={viewer.url}
                className="w-full h-[75vh] bg-[#F5F4F0]"
              />
            )}
          </div>
        </Modal>
      )}
    </SectionCard>
  );
};

export default SignedDocumentsSection;
